import { useEffect, useState, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
    DialogFooter,
} from "@/components/ui/dialog";
import { formatPoints } from "@/lib/format";
import { formatPesos } from "@/lib/currency";
import { toast } from "sonner";
import {
    Vault, TrendingUp, Calendar, CalendarRange, CalendarDays,
    Download, Plus, History, Sliders, BarChart3,
    Pencil, Trash2, ShieldAlert,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";

export default function AdminTreasuryTab() {
    const [summary, setSummary] = useState(null);
    const [byMarket, setByMarket] = useState(null);
    const [movements, setMovements] = useState(null);
    const [filterMarket, setFilterMarket] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [loading, setLoading] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);

    const load = async () => {
        try {
            const [s, b] = await Promise.all([
                api.get("/admin/treasury/summary"),
                api.get("/admin/treasury/by-market"),
            ]);
            setSummary(s.data);
            setByMarket(b.data.items || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar tesorería");
        }
    };
    const loadMovements = async () => {
        try {
            const params = new URLSearchParams();
            if (filterMarket) params.append("market_id", filterMarket);
            if (startDate) params.append("start_date", startDate);
            if (endDate) params.append("end_date", endDate);
            const { data } = await api.get(`/admin/treasury/movements?${params.toString()}`);
            setMovements(data.items || []);
        } catch {
            toast.error("Error al cargar movimientos");
        }
    };
    const reloadAll = () => setReloadKey((k) => k + 1);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [s, b] = await Promise.all([
                    api.get("/admin/treasury/summary"),
                    api.get("/admin/treasury/by-market"),
                ]);
                setSummary(s.data);
                setByMarket(b.data.items || []);
            } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar tesorería");
            } finally {
                setLoading(false);
            }
        })();
    }, [reloadKey]);
    useEffect(() => {
        (async () => {
            try {
                const params = new URLSearchParams();
                if (filterMarket) params.append("market_id", filterMarket);
                if (startDate) params.append("start_date", startDate);
                if (endDate) params.append("end_date", endDate);
                const { data } = await api.get(`/admin/treasury/movements?${params.toString()}`);
                setMovements(data.items || []);
            } catch {
                toast.error("Error al cargar movimientos");
            }
        })();
    }, [filterMarket, startDate, endDate, reloadKey]);

    const exportCsv = () => {
        const params = new URLSearchParams();
        if (filterMarket) params.append("market_id", filterMarket);
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
        // open download via fetch with credentials so axios cookie auth applies
        api.get(`/admin/treasury/movements.csv?${params.toString()}`, { responseType: "blob" })
            .then((res) => {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement("a");
                a.href = url;
                a.download = `treasury-${Date.now()}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch(() => toast.error("Error al exportar"));
    };

    if (loading || !summary) {
        return <div className="h-72 rounded-2xl bg-p2w-surface border border-p2w-line animate-pulse" />;
    }

    return (
        <div className="space-y-6" data-testid="admin-treasury-tab">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <KpiCard
                    icon={Vault}
                    label="Saldo de la Caja"
                    points={summary.balance}
                    highlight
                    sublabel="Acumulado total"
                    testid="kpi-balance"
                />
                <KpiCard icon={Calendar} label="Ingresos · Hoy" points={summary.income.today} sublabel="desde 00:00 UTC" testid="kpi-today" />
                <KpiCard icon={CalendarRange} label="Ingresos · Semana" points={summary.income.week} sublabel="desde lunes" testid="kpi-week" />
                <KpiCard icon={CalendarDays} label="Ingresos · Mes" points={summary.income.month} sublabel="mes en curso" testid="kpi-month" />
            </div>

            {/* Chart 30-day + Distribution config */}
            <div className="grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 rounded-2xl border border-p2w-line bg-p2w-surface p-5" data-testid="treasury-chart">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-p2w-purple" />
                            <h3 className="font-sora font-bold text-white">Evolución · últimos 30 días</h3>
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-p2w-gray">
                            Total ventana: <span className="text-p2w-green font-bold number-tabular">{formatPoints(summary.chart_30d.reduce((a, c) => a + c.amount, 0))} pts</span>
                        </div>
                    </div>
                    <BarChart points={summary.chart_30d} />
                </div>
                <DistributionConfig pcts={summary.distribution} onChanged={reloadAll} />
            </div>

            {/* By-market table */}
            <div className="rounded-2xl border border-p2w-line bg-p2w-surface overflow-hidden" data-testid="treasury-by-market">
                <div className="px-5 py-3.5 border-b border-p2w-line flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-p2w-green" />
                    <h3 className="font-sora font-bold text-white">Desglose por mercado</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-left text-[10px] uppercase tracking-wider text-p2w-gray border-b border-p2w-line bg-p2w-bg/40">
                            <tr>
                                <th className="px-5 py-3">Mercado</th>
                                <th className="px-5 py-3 text-center">Pools resueltos</th>
                                <th className="px-5 py-3 text-right">Pozo total movido</th>
                                <th className="px-5 py-3 text-center">% Plataforma</th>
                                <th className="px-5 py-3 text-right">Acreditado a Caja</th>
                            </tr>
                        </thead>
                        <tbody>
                            {byMarket && byMarket.length > 0 ? byMarket.map((row) => (
                                <tr key={row.market_id} className="border-t border-p2w-line/50 hover:bg-p2w-bg/30">
                                    <td className="px-5 py-3">
                                        <button
                                            onClick={() => setFilterMarket(row.market_id === "__manual__" ? "" : row.market_id)}
                                            className="text-left hover:text-p2w-green transition"
                                        >
                                            <div className="font-medium text-white truncate max-w-md">{row.title}</div>
                                            {row.category && <div className="text-[11px] text-p2w-gray">{row.category.name}</div>}
                                        </button>
                                    </td>
                                    <td className="px-5 py-3 text-center text-white number-tabular">{row.pools_resolved}</td>
                                    <td className="px-5 py-3 text-right text-p2w-gray number-tabular">{formatPoints(row.pozo_total)} pts</td>
                                    <td className="px-5 py-3 text-center text-p2w-gray">{(summary.distribution.platform_pct * 100).toFixed(1)}%</td>
                                    <td className="px-5 py-3 text-right font-sora font-bold text-p2w-green number-tabular">+{formatPoints(row.platform_credits)} pts</td>
                                </tr>
                            )) : (
                                <tr><td colSpan={5} className="px-5 py-10 text-center text-p2w-gray">Aún no hay movimientos a la Caja.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Movements list + filters + export + manual */}
            <div className="rounded-2xl border border-p2w-line bg-p2w-surface overflow-hidden" data-testid="treasury-movements">
                <div className="px-5 py-3.5 border-b border-p2w-line flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-2">
                        <History className="w-4 h-4 text-p2w-purple" />
                        <h3 className="font-sora font-bold text-white">Historial de movimientos</h3>
                        {filterMarket && (
                            <button onClick={() => setFilterMarket("")} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-p2w-elevated border border-p2w-line text-p2w-gray hover:text-white">
                                ✕ filtro mercado
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value ? `${e.target.value}T00:00:00+00:00` : "")}
                            className="bg-p2w-elevated border-p2w-line text-white h-9 w-40"
                            data-testid="treasury-filter-start"
                        />
                        <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value ? `${e.target.value}T23:59:59+00:00` : "")}
                            className="bg-p2w-elevated border-p2w-line text-white h-9 w-40"
                            data-testid="treasury-filter-end"
                        />
                        <Button onClick={exportCsv} size="sm" variant="outline" className="border-p2w-line bg-p2w-elevated text-white hover:bg-p2w-line h-9" data-testid="treasury-export-csv">
                            <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
                        </Button>
                        <ManualMovementDialog onDone={reloadAll} />
                    </div>
                </div>
                <div className="overflow-x-auto max-h-[500px]">
                    <table className="w-full text-sm">
                        <thead className="text-left text-[10px] uppercase tracking-wider text-p2w-gray sticky top-0 bg-p2w-surface border-b border-p2w-line">
                            <tr>
                                <th className="px-5 py-3">Fecha</th>
                                <th className="px-5 py-3">Tipo</th>
                                <th className="px-5 py-3">Mercado</th>
                                <th className="px-5 py-3 text-right">Monto</th>
                                <th className="px-5 py-3 text-right">Saldo resultante</th>
                                <th className="px-5 py-3 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {movements && movements.length > 0 ? movements.map((m) => (
                                <tr key={m.id} className="border-t border-p2w-line/40 hover:bg-p2w-bg/30">
                                    <td className="px-5 py-2.5 text-p2w-gray text-xs number-tabular whitespace-nowrap">{formatDt(m.created_at)}</td>
                                    <td className="px-5 py-2.5">
                                        <TypeBadge type={m.type} />
                                    </td>
                                    <td className="px-5 py-2.5 text-p2w-gray truncate max-w-xs">{m.market_title || (m.market_id ? <span className="font-mono text-xs">{m.market_id.slice(0, 8)}…</span> : "—")}</td>
                                    <td className={`px-5 py-2.5 text-right number-tabular font-bold ${m.amount >= 0 ? "text-p2w-green" : "text-rose-400"}`}>
                                        {m.amount >= 0 ? "+" : ""}{formatPoints(m.amount)}
                                    </td>
                                    <td className="px-5 py-2.5 text-right number-tabular text-white">{formatPoints(m.balance_after)}</td>
                                    <td className="px-5 py-2.5 text-right">
                                        <MovementRowActions movement={m} onDone={reloadAll} />
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={6} className="px-5 py-10 text-center text-p2w-gray">Sin movimientos en el rango.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Audit log */}
            <AuditLogSection reloadKey={reloadKey} />
        </div>
    );
}

function AuditLogSection({ reloadKey }) {
    const [items, setItems] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const { data } = await api.get("/admin/treasury/audit-log?limit=300");
                setItems(data.items || []);
            } catch {
                setItems([]);
            }
        })();
    }, [open, reloadKey]);

    return (
        <div className="rounded-2xl border border-p2w-line bg-p2w-surface overflow-hidden" data-testid="treasury-audit-section">
            <button
                onClick={() => setOpen((o) => !o)}
                className="w-full px-5 py-3.5 border-b border-p2w-line flex items-center justify-between hover:bg-p2w-elevated transition"
                data-testid="audit-toggle"
            >
                <div className="flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4 text-amber-400" />
                    <h3 className="font-sora font-bold text-white">Histórico de auditoría</h3>
                    <span className="text-[10px] uppercase tracking-wider text-p2w-gray">cambios + archivos</span>
                </div>
                <span className="text-xs text-p2w-gray">{open ? "Ocultar ▴" : "Mostrar ▾"}</span>
            </button>
            {open && (
                <div className="overflow-x-auto max-h-[500px]">
                    {items === null ? (
                        <div className="px-5 py-10 text-center text-p2w-gray text-sm">Cargando…</div>
                    ) : items.length === 0 ? (
                        <div className="px-5 py-10 text-center text-p2w-gray text-sm">Sin registros de auditoría.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="text-left text-[10px] uppercase tracking-wider text-p2w-gray sticky top-0 bg-p2w-surface border-b border-p2w-line">
                                <tr>
                                    <th className="px-5 py-3">Fecha</th>
                                    <th className="px-5 py-3">Acción</th>
                                    <th className="px-5 py-3">Mercado</th>
                                    <th className="px-5 py-3 text-right">Detalle</th>
                                    <th className="px-5 py-3">Actor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((e, i) => (
                                    <tr key={i} className="border-t border-p2w-line/40 hover:bg-p2w-bg/30">
                                        <td className="px-5 py-2.5 text-p2w-gray text-xs number-tabular whitespace-nowrap">{formatDt(e.at)}</td>
                                        <td className="px-5 py-2.5">
                                            <AuditActionBadge kind={e.kind} reason={e.reason} />
                                        </td>
                                        <td className="px-5 py-2.5 text-p2w-gray truncate max-w-xs">{e.market_title || (e.market_id ? <span className="font-mono text-xs">{e.market_id.slice(0, 8)}…</span> : "—")}</td>
                                        <td className="px-5 py-2.5 text-right text-xs">
                                            {e.kind === "edited" ? (
                                                <div className="font-sora">
                                                    <span className="text-p2w-gray line-through number-tabular">{formatAmt(e.amount_from)}</span>
                                                    {" → "}
                                                    <span className="text-p2w-green font-bold number-tabular">{formatAmt(e.amount_to)}</span>
                                                    {e.note_to && <div className="text-[10px] text-p2w-gray mt-0.5 truncate max-w-[260px]">&ldquo;{e.note_to}&rdquo;</div>}
                                                </div>
                                            ) : (
                                                <div className={`number-tabular font-bold ${e.amount >= 0 ? "text-p2w-green" : "text-rose-400"}`}>{formatAmt(e.amount)}</div>
                                            )}
                                        </td>
                                        <td className="px-5 py-2.5 text-xs text-p2w-gray">
                                            {e.actor_info ? (
                                                <span title={e.actor_info.email}>@{e.actor_info.username}</span>
                                            ) : (
                                                <span className="text-p2w-gray/60">sistema</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
}

function AuditActionBadge({ kind, reason }) {
    let label = "evento";
    let cls = "text-p2w-gray border-p2w-line bg-p2w-elevated";
    if (kind === "edited") { label = "Editado"; cls = "text-p2w-purple border-p2w-purple/40 bg-p2w-purple/10"; }
    else if (kind === "archived") {
        if (reason === "admin_manual_delete") { label = "Eliminado"; cls = "text-rose-400 border-rose-400/40 bg-rose-400/10"; }
        else if (reason && reason.startsWith("admin_reset")) { label = "Reset"; cls = "text-amber-400 border-amber-400/40 bg-amber-400/10"; }
        else { label = "Archivado"; cls = "text-p2w-gray border-p2w-line bg-p2w-elevated"; }
    }
    return <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold border ${cls}`}>{label}</span>;
}

function formatAmt(n) {
    if (n === null || n === undefined) return "—";
    const v = Number(n);
    return `${v >= 0 ? "+" : ""}${v.toLocaleString("es-CO")}`;
}

function MovementRowActions({ movement, onDone }) {
    const [editing, setEditing] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [amount, setAmount] = useState(String(movement.amount));
    const [note, setNote] = useState(movement.meta?.note || "");
    const [saving, setSaving] = useState(false);

    const submitEdit = async () => {
        const amt = parseInt(amount, 10);
        if (!Number.isFinite(amt)) { toast.error("Monto inválido"); return; }
        setSaving(true);
        try {
            await api.patch(`/admin/treasury/movements/${movement.id}`, { amount: amt, note });
            toast.success("Movimiento editado");
            setEditing(false);
            onDone?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al editar");
        } finally { setSaving(false); }
    };

    const submitDelete = async () => {
        setSaving(true);
        try {
            await api.delete(`/admin/treasury/movements/${movement.id}`);
            toast.success("Movimiento eliminado");
            setConfirming(false);
            onDone?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al eliminar");
        } finally { setSaving(false); }
    };

    return (
        <div className="flex items-center gap-1 justify-end">
            <Dialog open={editing} onOpenChange={setEditing}>
                <DialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-p2w-purple hover:bg-p2w-purple/10 hover:text-p2w-purple" data-testid={`treasury-edit-${movement.id.slice(0,6)}`} title="Editar movimiento">
                        <Pencil className="w-3.5 h-3.5" />
                    </Button>
                </DialogTrigger>
                <DialogContent className="bg-p2w-surface border-p2w-line text-white">
                    <DialogHeader>
                        <DialogTitle className="font-sora">Editar movimiento contable</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="text-xs text-p2w-gray bg-p2w-elevated rounded-lg px-3 py-2 border border-p2w-line">
                            <div>ID: <span className="font-mono text-white">{movement.id.slice(0, 8)}…</span></div>
                            <div>Fecha: <span className="text-white">{formatDt(movement.created_at)}</span></div>
                            <div>Saldo actual luego de este movimiento: <span className="text-white number-tabular">{formatPoints(movement.balance_after)}</span></div>
                        </div>
                        <div>
                            <Label className="text-xs text-p2w-gray">Monto (pts)</Label>
                            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-p2w-elevated border-p2w-line text-white" data-testid="treasury-edit-amount" />
                            <div className="text-[10px] text-p2w-gray mt-1">El cambio se aplica al saldo y se recalculan los saldos posteriores. Queda histórico en auditoría.</div>
                        </div>
                        <div>
                            <Label className="text-xs text-p2w-gray">Nota / justificación</Label>
                            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Razón del cambio…" className="bg-p2w-elevated border-p2w-line text-white" data-testid="treasury-edit-note" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(false)} className="border-p2w-line bg-p2w-elevated text-white">Cancelar</Button>
                        <Button onClick={submitEdit} disabled={saving} className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90" data-testid="treasury-edit-submit">
                            {saving ? "..." : "Guardar cambios"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={confirming} onOpenChange={setConfirming}>
                <DialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-400 hover:bg-rose-400/10 hover:text-rose-400" data-testid={`treasury-delete-${movement.id.slice(0,6)}`} title="Eliminar movimiento">
                        <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                </DialogTrigger>
                <DialogContent className="bg-p2w-surface border-p2w-line text-white">
                    <DialogHeader>
                        <DialogTitle className="font-sora text-rose-400">¿Eliminar este movimiento?</DialogTitle>
                    </DialogHeader>
                    <div className="text-sm text-p2w-gray">
                        <p>Vas a eliminar el movimiento de <span className={`font-bold number-tabular ${movement.amount >= 0 ? "text-p2w-green" : "text-rose-400"}`}>{movement.amount >= 0 ? "+" : ""}{formatPoints(movement.amount)} pts</span>. El saldo de la Caja se recalculará automáticamente. La entrada se archiva (auditoría conserva el histórico).</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirming(false)} className="border-p2w-line bg-p2w-elevated text-white">Cancelar</Button>
                        <Button onClick={submitDelete} disabled={saving} className="bg-rose-500 text-white hover:bg-rose-500/90" data-testid="treasury-delete-confirm">
                            {saving ? "..." : "Eliminar"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function KpiCard({ icon: Icon, label, points, sublabel, highlight, testid }) {
    return (
        <div
            data-testid={testid}
            className={`rounded-2xl border p-4 backdrop-blur-sm transition ${
                highlight
                    ? "border-p2w-green/40 bg-gradient-to-br from-p2w-green/15 via-p2w-green/5 to-transparent shadow-glow-green"
                    : "border-p2w-line bg-p2w-surface hover:border-p2w-purple/40"
            }`}
        >
            <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest text-p2w-gray font-semibold">{label}</span>
                <Icon className={`w-4 h-4 ${highlight ? "text-p2w-green" : "text-p2w-purple"}`} />
            </div>
            <div className={`mt-2 font-sora font-extrabold text-2xl number-tabular ${highlight ? "text-p2w-green" : "text-white"}`}>
                {formatPoints(points)}
            </div>
            <div className="text-[11px] text-p2w-gray mt-0.5 number-tabular">≈ {formatPesos(points)}</div>
            {sublabel && <div className="mt-1 text-[10px] text-p2w-gray/70 uppercase tracking-wider">{sublabel}</div>}
        </div>
    );
}

function BarChart({ points }) {
    const max = Math.max(1, ...points.map((p) => p.amount));
    return (
        <div className="mt-4 flex items-end gap-1 h-32" data-testid="treasury-bars">
            {points.map((p, i) => {
                const h = max > 0 ? Math.max(2, (p.amount / max) * 100) : 2;
                return (
                    <div key={i} className="flex-1 group relative">
                        <div
                            className={`w-full rounded-t transition-all hover:opacity-80 ${p.amount > 0 ? "bg-gradient-to-t from-p2w-green/60 to-p2w-green" : "bg-p2w-line/50"}`}
                            style={{ height: `${h}%` }}
                        />
                        {p.amount > 0 && (
                            <div className="opacity-0 group-hover:opacity-100 transition absolute -top-12 left-1/2 -translate-x-1/2 z-10 whitespace-nowrap text-[10px] bg-p2w-bg border border-p2w-line rounded-lg px-2 py-1 shadow-xl">
                                <div className="text-p2w-gray">{p.date.slice(5)}</div>
                                <div className="text-p2w-green font-bold number-tabular">+{formatPoints(p.amount)}</div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function DistributionConfig({ pcts, onChanged }) {
    const [platform, setPlatform] = useState((pcts.platform_pct * 100).toFixed(1));
    const [creator, setCreator] = useState((pcts.creator_pct * 100).toFixed(1));
    const [league, setLeague] = useState((pcts.league_pct * 100).toFixed(1));
    const [saving, setSaving] = useState(false);

    const players = useMemo(() => {
        const sum = (Number(platform) || 0) + (Number(creator) || 0) + (Number(league) || 0);
        return Math.max(0, 100 - sum);
    }, [platform, creator, league]);
    const invalid = (Number(platform) + Number(creator) + Number(league)) >= 100 || players <= 0;

    const save = async () => {
        if (invalid) { toast.error("Suma > 100 o sin margen para jugadores"); return; }
        setSaving(true);
        try {
            await api.patch("/admin/settings", {
                platform_pct: Number(platform) / 100,
                creator_pct: Number(creator) / 100,
                league_pct: Number(league) / 100,
            });
            toast.success("Reparto actualizado");
            onChanged?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
        } finally { setSaving(false); }
    };

    return (
        <div className="rounded-2xl border border-p2w-line bg-p2w-surface p-5" data-testid="distribution-config">
            <div className="flex items-center gap-2">
                <Sliders className="w-4 h-4 text-p2w-purple" />
                <h3 className="font-sora font-bold text-white">Reparto del pozo</h3>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2.5">
                <PctField label="Plataforma" value={platform} setValue={setPlatform} accent="green" testid="pct-platform" />
                <PctField label="Creador" value={creator} setValue={setCreator} accent="purple" testid="pct-creator" />
                <PctField label="Liga" value={league} setValue={setLeague} accent="magenta" testid="pct-league" />
            </div>
            <div className={`mt-4 rounded-xl border p-3 ${invalid ? "border-rose-400/50 bg-rose-400/5" : "border-p2w-green/40 bg-p2w-green/5"}`}>
                <div className="text-[10px] uppercase tracking-wider text-p2w-gray">Reparto resultante</div>
                <div className="mt-1 text-xs sm:text-sm font-semibold text-white flex flex-wrap gap-x-3 gap-y-1" data-testid="distribution-result">
                    <span>Jugadores <span className={`number-tabular ${invalid ? "text-rose-400" : "text-p2w-green"}`}>{players.toFixed(1)}%</span></span>
                    <span>·</span>
                    <span>Plataforma <span className="text-p2w-green number-tabular">{platform}%</span></span>
                    <span>·</span>
                    <span>Creador <span className="text-p2w-purple number-tabular">{creator}%</span></span>
                    <span>·</span>
                    <span>Liga <span className="text-p2w-magenta number-tabular">{league}%</span></span>
                </div>
            </div>
            <Button onClick={save} disabled={saving || invalid} className="mt-4 w-full bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold" data-testid="save-distribution">
                {saving ? "Guardando…" : "Guardar reparto"}
            </Button>
        </div>
    );
}

function PctField({ label, value, setValue, accent, testid }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-wider text-p2w-gray font-semibold">{label} %</Label>
            <Input
                type="number"
                min="0" max="95" step="0.1"
                value={value}
                data-testid={testid}
                onChange={(e) => setValue(e.target.value)}
                className="mt-1 bg-p2w-elevated border-p2w-line text-white h-9 text-center number-tabular font-sora font-bold"
            />
        </div>
    );
}

function ManualMovementDialog({ onDone }) {
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState("");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        const amt = Number(amount);
        if (!amt) { toast.error("Monto requerido"); return; }
        if (!note || note.length < 3) { toast.error("Nota mínima 3 caracteres"); return; }
        setSaving(true);
        try {
            await api.post("/admin/treasury/manual", { amount: Math.round(amt), note });
            toast.success(`Movimiento registrado: ${amt >= 0 ? "+" : ""}${formatPoints(amt)} pts`);
            setOpen(false);
            setAmount(""); setNote("");
            onDone?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm" className="bg-p2w-purple text-white hover:bg-p2w-purple/90 h-9" data-testid="treasury-manual-open">
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Manual
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-p2w-surface border-p2w-line text-white">
                <DialogHeader>
                    <DialogTitle className="font-sora">Movimiento manual a la Caja</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs text-p2w-gray">Monto (pts)</Label>
                        <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Ej: 5000 o -1500 (ajuste)" className="bg-p2w-elevated border-p2w-line text-white" data-testid="manual-amount" />
                        <div className="mt-1 text-[10px] text-p2w-gray">Positivo agrega, negativo descuenta. Queda registrado para auditoría.</div>
                    </div>
                    <div>
                        <Label className="text-xs text-p2w-gray">Nota / justificación *</Label>
                        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej: Ajuste contable mes X, devolución a usuario Y..." className="bg-p2w-elevated border-p2w-line text-white" data-testid="manual-note" />
                    </div>
                </div>
                <DialogFooter>
                    <Button onClick={submit} disabled={saving} className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90" data-testid="manual-submit">
                        {saving ? "..." : "Registrar"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function TypeBadge({ type }) {
    const map = {
        platform_fee: { label: "Comisión", color: "text-p2w-green border-p2w-green/40 bg-p2w-green/10" },
        manual_adjustment: { label: "Manual", color: "text-p2w-purple border-p2w-purple/40 bg-p2w-purple/10" },
    };
    const info = map[type] || { label: type, color: "text-p2w-gray border-p2w-line bg-p2w-elevated" };
    return <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold border ${info.color}`}>{info.label}</span>;
}

function formatDt(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("es-CO", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return iso.slice(0, 16).replace("T", " "); }
}
